import { useCallback, useState } from "react"
import { observer } from "mobx-react-lite"
import { Link, useNavigate } from "react-router-dom"

import { Button } from "@/components/button"
import { TooltipProvider } from "@/components/tooltip"
import { useScaleApplication } from "@/domains/application/use-scale-application"
import { MetaData } from "@/domains/common/layout/metadata"
import { PageContainer } from "@/domains/common/layout/page-container"
import { PageTitle } from "@/domains/common/layout/page-title"
import { ScaleCard300 } from "@/domains/design/components/scale-card-300"
import { LiquidityTableParams } from "@/domains/liquidity-page/components/liquidity-table-params"
import LiquidityTable from "@/domains/liquidity-page/components/liquidity-table/liquidity-table"
import { LiquidityPageProvider } from "@/domains/liquidity-page/context"
import { MinimalPoolStore } from "@/domains/minimal-pool"
import { ZapDialogPage } from "@/domains/minimal-pools-zap/components/zap-dialog-page"
import { useGovernanceToken } from "@/hooks/use-governance-token"
import useResponsive from "@/hooks/use-responsive"

export type HandleZapClick = (pool: MinimalPoolStore) => void

const LiquidityPageContent = observer(() => {
  const navigate = useNavigate()
  const governanceToken = useGovernanceToken()
  const { isMobile } = useResponsive()
  const [selectedPool, setSelectedPool] = useState<MinimalPoolStore | null>(null)
  const [showZapDialog, setShowZapDialog] = useState(false)

  const onCreate = () => {
    navigate("/liquidity/create")
  }

  const handleZapClick: HandleZapClick = useCallback(
    (pool: MinimalPoolStore) => {
      setSelectedPool(pool)
      setShowZapDialog(true)
    },
    [setSelectedPool, setShowZapDialog],
  )

  const title = "Classic Liquidity"

  return (
    <>
      <MetaData title={title} />
      <PageTitle
        title="Classic Liquidity"
        subTitle={`Add liquidity to pools and earn ${governanceToken.symbol}`}
        middleEndItem={
          <Button variant={"primary"} onClick={onCreate} className="min-w-fit text-nowrap" asChild>
            <Link href={"/liquidity/create"}>Create New Pool</Link>
          </Button>
        }
      />
      <TooltipProvider>
        {isMobile ? (
          <div className="flex flex-col gap-6 text-t100">
            <LiquidityTableParams />
            <LiquidityTable handleZapClick={handleZapClick} />
          </div>
        ) : (
          <ScaleCard300 className="flex w-full flex-col gap-6 rounded-lg border border-p300-light text-t100 sm:p-4 lg:p-6">
            <LiquidityTableParams />
            <LiquidityTable handleZapClick={handleZapClick} />
          </ScaleCard300>
        )}
        {selectedPool && (
          <ZapDialogPage
            isOpen={showZapDialog}
            pool={selectedPool}
            onClose={() => {
              setShowZapDialog(false)
              setSelectedPool(null)
            }}
          />
        )}
      </TooltipProvider>
    </>
  )
})

const LiquidityPage = observer(() => {
  const scaleApp = useScaleApplication()
  const zapOrchestrator = scaleApp.MinimalPools.ZapOrchestrator
  const tokenRepository = scaleApp.Tokens.Repository

  return (
    <PageContainer>
      <LiquidityPageProvider zapOrchestrator={zapOrchestrator} tokenRepository={tokenRepository}>
        <LiquidityPageContent />
      </LiquidityPageProvider>
    </PageContainer>
  )
})

export default LiquidityPage
